import { io } from 'socket.io-client';

const SOCKET_IO_MAX_HTTP_BUFFER_SIZE = 10 * 1024 * 1024;

/**
 * Enterprise-grade WebSocket Manager with heartbeat system
 * Implements FANG-level best practices for connection management
 */
class WebSocketManager {
  constructor(config = {}) {
    this.config = {
      url: config.url || process.env.REACT_APP_WS_URL,
      path: config.path || '/test-automation/socket.io/',
      transports: config.transports || ['websocket', 'polling'],
      withCredentials: config.withCredentials !== false,
      heartbeatInterval: config.heartbeatInterval || 30000,
      ...config
    };

    // Connection state
    this.socket = null;
    this.isConnected = false;
    this.isConnecting = false;

    // Heartbeat system (one-way ping only)
    this.heartbeatTimer = null;

    // Event listeners registry for cleanup
    this.eventListeners = new Map();
    this.internalListeners = new Set();

    // Connection callbacks
    this.onConnect = null;
    this.onDisconnect = null;
    this.onError = null;

    // Cleanup flag
    this.isDestroyed = false;

    // Bumped on every disconnect / new connect attempt so late connect callbacks
    // from a discarded socket cannot flip isConnected after a forced reconnect.
    this._connectionGeneration = 0;

    // Bind methods to preserve context
    this.connect = this.connect.bind(this);
    this.disconnect = this.disconnect.bind(this);
    this.emit = this.emit.bind(this);
    this.on = this.on.bind(this);
    this.off = this.off.bind(this);
    this.destroy = this.destroy.bind(this);
  }

  /**
   * Establish WebSocket connection with comprehensive error handling
   */
  async connect() {
    if (this.isDestroyed) {
      console.warn('WebSocketManager: Cannot connect - instance is destroyed');
      return false;
    }

    if (this.isConnected || this.isConnecting) {
      console.warn('WebSocketManager: Already connected or connecting');
      return true;
    }

    try {
      this.isConnecting = true;
      const generation = ++this._connectionGeneration;
      console.log('[WebSocketManager] Attempting to connect to:', this.config.url);
      console.log('[WebSocketManager] Path:', this.config.path);
      console.log('[WebSocketManager] With credentials:', this.config.withCredentials);
      this._log('Attempting to connect to WebSocket server...');

      // Create socket instance with configuration
      this.socket = io(this.config.url, {
        path: this.config.path,
        transports: this.config.transports,
        reconnection: false,
        withCredentials: this.config.withCredentials,
        forceNew: true,
        maxHttpBufferSize: SOCKET_IO_MAX_HTTP_BUFFER_SIZE,
      });

      // Set up internal event listeners
      this._setupInternalListeners();

      // Wait for connection (no timeout)
      return new Promise((resolve, reject) => {
        this.socket.once('connect', () => {
          if (generation !== this._connectionGeneration || this.isDestroyed) {
            resolve(false);
            return;
          }
          this._handleConnection();
          resolve(true);
        });

        this.socket.once('connect_error', (error) => {
          if (generation !== this._connectionGeneration || this.isDestroyed) {
            resolve(false);
            return;
          }
          this._handleConnectionError(error);
          reject(error);
        });
      });

    } catch (error) {
      this._log(`Connection failed: ${error.message}`, 'error');
      this.isConnecting = false;
      this._handleConnectionError(error);
      return false;
    }
  }

  /**
   * Gracefully disconnect from WebSocket server.
   * Always clears connection flags so the next connect() opens a fresh socket
   * (required after Stop → Generate / Replay — QPN-2087).
   */
  disconnect() {
    if (this.isDestroyed) return;

    this._log('Disconnecting from WebSocket server...');
    this._connectionGeneration += 1;
    this._stopHeartbeat();

    const socket = this.socket;
    this.socket = null;
    this.isConnected = false;
    this.isConnecting = false;

    if (socket) {
      try {
        for (const event of this.internalListeners) {
          socket.removeAllListeners(event);
        }
        socket.disconnect();
      } catch (error) {
        this._log(`Error during disconnect: ${error.message}`, 'warn');
      }
    }
  }

  /**
   * Emit event to server with error handling
   */
  emit(event, data, callback) {
    if (!this.isConnected || !this.socket || this.isDestroyed) {
      const error = new Error('WebSocket not connected');
      this._log(`Cannot emit '${event}': ${error.message}`, 'error');
      if (callback) callback(error);
      return false;
    }

    try {
      if (callback) {
        this.socket.emit(event, data, callback);
      } else {
        this.socket.emit(event, data);
      }
      return true;
    } catch (error) {
      this._log(`Error emitting '${event}': ${error.message}`, 'error');
      if (callback) callback(error);
      return false;
    }
  }

  /**
   * Register event listener with automatic cleanup tracking
   */
  on(event, callback) {
    if (!callback || typeof callback !== 'function') {
      throw new Error('Callback must be a function');
    }

    if (this.isDestroyed) {
      console.warn('WebSocketManager: Cannot add listener - instance is destroyed');
      return;
    }

    // Store listener for cleanup
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, new Set());
    }
    this.eventListeners.get(event).add(callback);

    // Add listener to socket if connected
    if (this.socket) {
      this.socket.on(event, callback);
    }
  }

  /**
   * Remove event listener
   */
  off(event, callback) {
    if (this.eventListeners.has(event)) {
      this.eventListeners.get(event).delete(callback);
      if (this.eventListeners.get(event).size === 0) {
        this.eventListeners.delete(event);
      }
    }

    if (this.socket) {
      this.socket.off(event, callback);
    }
  }

  /**
   * Remove all listeners for an event
   */
  removeAllListeners(event) {
    if (event) {
      this.eventListeners.delete(event);
      if (this.socket) {
        this.socket.removeAllListeners(event);
      }
    } else {
      this.eventListeners.clear();
      if (this.socket) {
        this.socket.removeAllListeners();
      }
    }
  }

  /**
   * Get connection status
   */
  getConnectionStatus() {
    return {
      isConnected: this.isConnected,
      isConnecting: this.isConnecting
    };
  }

  /**
   * Destroy the WebSocket manager and cleanup all resources
   */
  destroy() {
    if (this.isDestroyed) return;

    this._log('Destroying WebSocket manager...');
    this.isDestroyed = true;

    // Stop heartbeat
    this._stopHeartbeat();

    // Clear all event listeners
    this.removeAllListeners();

    // Disconnect socket
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }

    // Reset state
    this.isConnected = false;
    this.isConnecting = false;

    // Clear callbacks
    this.onConnect = null;
    this.onDisconnect = null;
    this.onError = null;

    this._log('WebSocket manager destroyed');
  }

  /**
   * Set up internal event listeners for connection management
   */
  _setupInternalListeners() {
    if (!this.socket) return;

    // Connection established
    this.socket.on('connect', () => {
      this._handleConnection();
    });

    // Connection lost
    this.socket.on('disconnect', (reason) => {
      this._handleDisconnection(reason);
    });

    // Connection error
    this.socket.on('connect_error', (error) => {
      this._handleConnectionError(error);
    });

    // Track internal listeners for cleanup
    this.internalListeners.add('connect');
    this.internalListeners.add('disconnect');
    this.internalListeners.add('connect_error');
  }

  /**
   * Handle successful connection
   */
  _handleConnection() {
    if (!this.socket || this.isDestroyed) return;

    this.isConnected = true;
    this.isConnecting = false;

    this._log('Connected to WebSocket server');
    
    // Re-register all event listeners
    this._reregisterEventListeners();
    
    // Start heartbeat (one-way ping)
    this._startHeartbeat();

    // Call connection callback
    if (this.onConnect) {
      try {
        this.onConnect();
      } catch (error) {
        this._log(`Error in onConnect callback: ${error.message}`, 'error');
      }
    }
  }

  /**
   * Handle disconnection
   */
  _handleDisconnection(reason) {
    const wasConnected = this.isConnected;
    this.isConnected = false;
    this.isConnecting = false;

    this._log(`Disconnected from WebSocket server: ${reason}`);
    
    // Stop heartbeat
    this._stopHeartbeat();

    // Call disconnection callback
    if (this.onDisconnect && wasConnected) {
      try {
        this.onDisconnect(reason);
      } catch (error) {
        this._log(`Error in onDisconnect callback: ${error.message}`, 'error');
      }
    }
  }

  /**
   * Handle connection errors
   */
  _handleConnectionError(error) {
    this.isConnecting = false;
    this._log(`Connection error: ${error.message}`, 'error');

    // Call error callback
    if (this.onError) {
      try {
        this.onError(error);
      } catch (callbackError) {
        this._log(`Error in onError callback: ${callbackError.message}`, 'error');
      }
    }
  }

  /**
   * Start heartbeat system (one-way ping only)
   */
  _startHeartbeat() {
    if (this.heartbeatTimer) return;

    this.heartbeatTimer = setInterval(() => {
      if (!this.isConnected || !this.socket || this.isDestroyed) {
        this._stopHeartbeat();
        return;
      }

      // Send one-way ping (no response tracking)
      this.socket.emit('ping', Date.now());
    }, this.config.heartbeatInterval);
  }

  /**
   * Stop heartbeat system
   */
  _stopHeartbeat() {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }


  /**
   * Re-register all event listeners
   */
  _reregisterEventListeners() {
    if (!this.socket) return;

    for (const [event, callbacks] of this.eventListeners) {
      for (const callback of callbacks) {
        this.socket.on(event, callback);
      }
    }
  }

  /**
   * Cleanup socket resources
   */
  _cleanup() {
    if (this.socket) {
      // Remove only internal listeners, keep user listeners for re-registration
      for (const event of this.internalListeners) {
        this.socket.removeAllListeners(event);
      }
    }
    this.isConnected = false;
    this.isConnecting = false;
  }

  /**
   * Logging utility
   */
  _log(message, level = 'info') {
    const timestamp = new Date().toISOString();
    const prefix = `[WebSocketManager ${timestamp}]`;
    
    switch (level) {
      case 'error':
        console.error(`${prefix} ${message}`);
        break;
      case 'warn':
        console.warn(`${prefix} ${message}`);
        break;
      default:
        console.log(`${prefix} ${message}`);
    }
  }
}

export default WebSocketManager;
